import { nonEmptyArray, option } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import {
    CreateReservationGroupParameters,
    CreditCardData,
    PromotedServiceReservationParameters,
    ReservationGroupResult,
    ReservationParameters,
} from '@mews-distributor/data-access';
import { RecaptchaAction } from '@mews/framework';
import { DateTimeUtc, DateTimeUtils } from '@mews/framework/datetime';
import { createAction } from '@mews/framework/redux';
import { getCurrentJourneyTracker } from '@mews/framework/observability';
import {
    getAvailabilityBlockIdFromQuery,
    getLanguageCode,
    getReservationGroupIdFromQuery,
} from '../../modules/app/selectors';
import { getSelectedHotelConfigurationId } from '../../modules/configuration/selectors';
import { getReservationsList } from '../../modules/entities/reservations/selectors';
import { getEnterpriseServiceId, hasEnterpriseDayUseServices } from '../../modules/entities/services/selectors';
import { getPromotedServiceReservations } from '../../modules/promotedServiceReservations/selectors';
import {
    CREATE_RESERVATION_GROUP_FAILED,
    CREATE_RESERVATION_GROUP_STARTED,
    CREATE_RESERVATION_GROUP_SUCCEEDED,
    FETCH_RESERVATION_GROUP_FAILED,
    FETCH_RESERVATION_GROUP_STARTED,
    FETCH_RESERVATION_GROUP_SUCCEEDED,
} from '../../modules/reservationGroups/actionTypes';
import { getSelectedHotelId } from '../../modules/resourceOrder/selectors';
import { ROOM_MAX_AVAILABLE_COUNT_CHANGED } from '../../modules/serviceAvailability/actionTypes';
import { isValidAvailabilityBlock } from '../../selectors/views/availabilityBlocks';
import { getSelectedEnterpriseTimezone } from '../../selectors/views/selectedEnterprise/selectedEnterprise';
import { isRecaptchaVerificationEnabled } from '../../services/featureFlagService/featureFlags/recaptcha';
import {
    ApplicationState,
    DistributorPromiseThunkAction,
    DistributorThunkDispatch,
    ThunkExtraArgument,
} from '../../types';
import { toUtc } from '../../utils/dates';
import {
    getBookerEmail,
    getBookerFirstName,
    getBookerLastName,
    getBookerPhone,
    getCustomerEmail,
    getCustomerFirstName,
    getCustomerLastName,
    getCustomerNationalityCode,
    getCustomerNotes,
    getCustomerPhone,
    getSendMarketingEmails,
    isBookerEnabled,
} from '../../views/checkout/data/selectors';
import { getExceedingReservationsData } from '../apiUtils/getExceedingReservationsData';
import { logSentryError } from '../../sentry/sentry';

interface ExceedingReservationsCount {
    availabilityHash: string;
    roomCategoryId: string;
    requestedCount: number;
    exceedingCount: number;
    serviceAvailabilityHash: string;
    startUtc: DateTimeUtc;
    endUtc: DateTimeUtc;
    startTimeUtc?: string;
    endTimeUtc?: string;
}

const updateAvailabilityData =
    (exceedingReservationsCounts: ExceedingReservationsCount[], isDayUse: boolean, timezone: string) =>
    (dispatch: DistributorThunkDispatch, _: () => ApplicationState) => {
        exceedingReservationsCounts.forEach(
            ({
                availabilityHash,
                roomCategoryId,
                requestedCount,
                exceedingCount,
                serviceAvailabilityHash,
                startUtc,
                endUtc,
                startTimeUtc,
                endTimeUtc,
            }) => {
                const maxAvailableCount = requestedCount - exceedingCount;
                const availableCount = maxAvailableCount - requestedCount;

                // Only for hourly services
                const startTime =
                    startTimeUtc != null ? DateTimeUtils.dateTimeUtcStringToTime(startTimeUtc, timezone) : undefined;
                const endTime =
                    endTimeUtc != null ? DateTimeUtils.dateTimeUtcStringToTime(endTimeUtc, timezone) : undefined;

                return dispatch(
                    createAction(ROOM_MAX_AVAILABLE_COUNT_CHANGED, {
                        availabilityHash,
                        serviceAvailabilityHash,
                        startUtc,
                        endUtc,
                        maxAvailableCount,
                        availableCount,
                        categoryId: roomCategoryId,
                        isDayUse,
                        timezone,
                        startTime,
                        endTime,
                    })
                );
            }
        );
    };

interface CreateReservationGroupParams {
    enterpriseId: string;
    creditCardData?: CreditCardData | null;
}

export function createReservationGroup({
    enterpriseId: hotelId,
    creditCardData = null,
}: CreateReservationGroupParams): DistributorPromiseThunkAction<ReservationGroupResult> {
    return async (
        dispatch: DistributorThunkDispatch,
        getState: () => ApplicationState,
        {
            apiService,
            appClockService,
            environmentService,
            featureFlagService,
            gainsightService,
            loggerService,
            recaptchaService,
        }: ThunkExtraArgument
    ) => {
        const state = getState();
        const configurationId = getSelectedHotelConfigurationId(state);
        const languageCode = getLanguageCode(state);
        const reservations = getReservationsList(state);
        const bookerEnabled = isBookerEnabled(state);
        const availabilityBlockId = getAvailabilityBlockIdFromQuery(state);
        const nowUtc = appClockService.getAppNowUtc();
        const serviceId = getEnterpriseServiceId(state, { enterpriseId: hotelId });

        const validAvailabilityBlockId =
            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            availabilityBlockId && isValidAvailabilityBlock(state, { availabilityBlockId, nowUtc })
                ? availabilityBlockId
                : undefined;
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        const timezone = getSelectedEnterpriseTimezone(state, {}) || 'Europe/London';
        const notes = getCustomerNotes(state);
        const finalReservations: ReservationParameters[] = reservations.map(
            ({
                orderId: identifier,
                roomId: roomCategoryId,
                startDate,
                endDate,
                voucherCode,
                adultCount,
                childCount,
                occupancyData,
                productIds,
                rateId,
                startTimeUtc,
                endTimeUtc,
            }) => ({
                identifier,
                roomCategoryId,
                startUtc: startTimeUtc ?? toUtc(startDate, timezone),
                endUtc: endTimeUtc ?? toUtc(endDate, timezone),
                voucherCode,
                adultCount,
                childCount,
                occupancyData,
                productIds,
                rateId,
                notes,
                availabilityBlockId: validAvailabilityBlockId,
            })
        );

        const promotedServiceReservations = timezone != null ? getPromotedServiceReservations(state, { timezone }) : [];
        const finalPromotedServiceReservations: PromotedServiceReservationParameters[] =
            timezone != null
                ? promotedServiceReservations.map(
                      ({ orderId, serviceId: promotedServiceAssignmentId, startUtc, endUtc, reservationId }) => ({
                          parentReservationIdentifier: orderId,
                          promotedServiceAssignmentId,
                          startUtc,
                          endUtc,
                          identifier: reservationId,
                      })
                  )
                : [];

        dispatch(createAction(CREATE_RESERVATION_GROUP_STARTED));
        if (timezone == null) {
            const error = new TypeError('enterprises timezone is not defined');
            dispatch(createAction(CREATE_RESERVATION_GROUP_FAILED, { error }));
            throw error;
        }

        let recaptchaVerificationToken: string | null = null;

        try {
            recaptchaVerificationToken = await recaptchaService.execute(RecaptchaAction.CheckOut);
        } catch (e) {
            const isRecaptchaEnabled = isRecaptchaVerificationEnabled(state, {
                environmentService,
                featureFlagService,
            });

            if (isRecaptchaEnabled) {
                throw e;
            } else {
                loggerService.log('recaptcha-execute-failed', 'Recaptcha execute failed', {
                    isRecaptchaEnabled: false,
                    error: e,
                });
            }
        }

        const requestBody: CreateReservationGroupParameters = {
            configurationId,
            hotelId,
            languageCode,
            reservations: finalReservations,
            promotedServiceReservations: finalPromotedServiceReservations,
            availabilityBlockId: validAvailabilityBlockId,
            customer: {
                email: getCustomerEmail(state),
                firstName: getCustomerFirstName(state),
                lastName: getCustomerLastName(state),
                telephone: getCustomerPhone(state),
                nationalityCode: getCustomerNationalityCode(state),
                sendMarketingEmails: bookerEnabled ? false : getSendMarketingEmails(state),
            },
            creditCardData,
            recaptchaVerificationToken,
        };

        if (bookerEnabled) {
            requestBody.booker = {
                email: getBookerEmail(state),
                firstName: getBookerFirstName(state),
                lastName: getBookerLastName(state),
                telephone: getBookerPhone(state),
                sendMarketingEmails: getSendMarketingEmails(state),
            };
        }

        // Track the final booking action and stop journey tracking when complete
        const journeyTracker = getCurrentJourneyTracker();
        const bookingPromise = apiService
            .createReservationGroup(requestBody)
            .then((response: ReservationGroupResult) => {
                dispatch(createAction(CREATE_RESERVATION_GROUP_SUCCEEDED, { ...response }));
                const reservationGroupId = response.reservationGroups[0]?.id;
                const customerId = response.reservationGroups[0]?.customerId;

                gainsightService.setGlobalContext({
                    customerId,
                    reservationGroupId,
                    reservationsIds: response.reservations.map((reservation) => reservation.id),
                });

                return response;
            })
            .catch((error) => {
                const typedError = error as { data?: { ExceedingReservationIndexes?: number[] } };
                const exceedingReservationIndexes = typedError.data?.ExceedingReservationIndexes;
                if (exceedingReservationIndexes != null && exceedingReservationIndexes.length > 0) {
                    if (serviceId == null) {
                        dispatch(
                            logSentryError({
                                error: new TypeError(
                                    'Cannot update availability data with exceeding reservation counts. ServiceId is nil.'
                                ),
                                extra: { configurationId, enterpriseId: hotelId, bookerEnabled, availabilityBlockId },
                            })
                        );
                    } else {
                        const requestedReservationsOption = pipe(
                            finalReservations,
                            nonEmptyArray.fromArray,
                            option.map((reservationParams) =>
                                pipe(
                                    reservationParams,
                                    nonEmptyArray.map((reservation) => ({
                                        roomCategoryId: reservation.roomCategoryId,
                                        startUtc: reservation.startUtc,
                                        endUtc: reservation.endUtc,
                                        voucherCode: reservation.voucherCode ?? undefined,
                                        startTimeUtc: undefined,
                                        endTimeUtc: undefined,
                                    }))
                                )
                            )
                        );

                        if (option.isSome(requestedReservationsOption)) {
                            const exceedingReservationCounts = getExceedingReservationsData({
                                requestedReservations: requestedReservationsOption.value,
                                exceedingReservationIndexes,
                                enterpriseId: hotelId,
                                timezone,
                                serviceId,
                            });
                            const isDayUse = hasEnterpriseDayUseServices(state, { enterpriseId: hotelId });
                            dispatch(updateAvailabilityData(exceedingReservationCounts, isDayUse, timezone));
                        }
                    }
                }

                dispatch(createAction(CREATE_RESERVATION_GROUP_FAILED, { error }));
                throw error;
            });

        return journeyTracker.trackAsyncAndStop(bookingPromise, 'createReservationGroup');
    };
}

export function fetchReservationGroup(): DistributorPromiseThunkAction {
    return async (dispatch, getState, { apiService, gainsightService }) => {
        const state = getState();
        const hotelId = getSelectedHotelId(state);
        const reservationGroupId = getReservationGroupIdFromQuery(state);

        dispatch(createAction(FETCH_RESERVATION_GROUP_STARTED));

        if (reservationGroupId == null) {
            const error = new Error('Confirmation screen - no reservation group ids received in url query.');
            dispatch(createAction(FETCH_RESERVATION_GROUP_FAILED, { error }));
            throw error;
        }

        return apiService
            .getReservationGroup({ hotelId, ids: [reservationGroupId] })
            .then((response) => {
                dispatch(createAction(FETCH_RESERVATION_GROUP_SUCCEEDED, { ...response }));
                const customerId = response.reservationGroups[0]?.customerId;
                const { reservations } = response;

                gainsightService.setGlobalContext({
                    customerId,
                    reservationGroupId,
                    reservationsIds: reservations.map((reservation) => reservation.id),
                });
            })
            .catch((error: unknown) => {
                dispatch(createAction(FETCH_RESERVATION_GROUP_FAILED, { error }));
                throw error;
            });
    };
}
